import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { TIKTOK_CREATOR_INFO } from '@/queries/publishing';

export type TikTokCreatorInfo = {
  creator_avatar_url?: string | null;
  creator_username?: string | null;
  creator_nickname?: string | null;
  privacy_level_options: string[];
  comment_disabled: boolean;
  duet_disabled: boolean;
  stitch_disabled: boolean;
  max_video_post_duration_sec?: number | null;
};

/** The TikTok Direct Post `post_info` flags sent with a publish mutation. */
export interface TikTokPostInfoVariables {
  privacyLevel: string;
  disableComment: boolean;
  disableDuet: boolean;
  disableStitch: boolean;
  brandOrganicToggle: boolean;
  brandContentToggle: boolean;
  isAigc: boolean;
}

/**
 * State + creator-info query for TikTok Direct Post (privacy, interactions,
 * commercial-content disclosure, AI label, music-usage declaration). Shared
 * by the video Direct Post dialog and the carousel publish dialog; render it
 * with `TikTokCreatorStatus` + `TikTokPostInfoFields`. `reset()` restores the
 * defaults (call it whenever the dialog target changes).
 */
export function useTikTokPostInfo({ open, socialConnectionId }: {
  open: boolean;
  socialConnectionId: string;
}) {
  const [privacyLevel, setPrivacyLevel] = useState('');
  const [allowComment, setAllowComment] = useState(false);
  const [allowDuet, setAllowDuet] = useState(false);
  const [allowStitch, setAllowStitch] = useState(false);
  const [commercial, setCommercial] = useState(false);
  const [yourBrand, setYourBrand] = useState(false);
  const [brandedContent, setBrandedContent] = useState(false);
  const [isAigc, setIsAigc] = useState(true);
  const [musicConfirmed, setMusicConfirmed] = useState(false);

  const reset = useCallback(() => {
    setPrivacyLevel('');
    setAllowComment(false);
    setAllowDuet(false);
    setAllowStitch(false);
    setCommercial(false);
    setYourBrand(false);
    setBrandedContent(false);
    setIsAigc(true);
    setMusicConfirmed(false);
  }, []);

  const { data: creatorData, loading: loadingCreator, error: creatorError } = useQuery(TIKTOK_CREATOR_INFO, {
    variables: { socialConnectionId },
    skip: !open || !socialConnectionId,
    fetchPolicy: 'network-only',
  });
  const creator = creatorData?.tiktokCreatorInfo as TikTokCreatorInfo | undefined;
  const privacyOptions = useMemo(
    () => creator?.privacy_level_options || [],
    [creator],
  );

  const brandedBlocksPrivate = commercial && brandedContent;
  const visiblePrivacyOptions = useMemo(() => privacyOptions.filter((option) => (
    !(brandedBlocksPrivate && option === 'SELF_ONLY')
  )), [privacyOptions, brandedBlocksPrivate]);

  useEffect(() => {
    if (privacyLevel && visiblePrivacyOptions.length > 0 && !visiblePrivacyOptions.includes(privacyLevel)) {
      setPrivacyLevel('');
    }
  }, [privacyLevel, visiblePrivacyOptions]);

  const commercialValid = !commercial || yourBrand || brandedContent;
  const isComplete = !!privacyLevel
    && visiblePrivacyOptions.includes(privacyLevel)
    && musicConfirmed
    && commercialValid;

  const variables: TikTokPostInfoVariables = {
    privacyLevel,
    disableComment: creator?.comment_disabled ? true : !allowComment,
    disableDuet: creator?.duet_disabled ? true : !allowDuet,
    disableStitch: creator?.stitch_disabled ? true : !allowStitch,
    brandOrganicToggle: commercial && yourBrand,
    brandContentToggle: commercial && brandedContent,
    isAigc,
  };

  return {
    creator,
    loadingCreator,
    creatorError,
    privacyLevel,
    setPrivacyLevel,
    visiblePrivacyOptions,
    brandedBlocksPrivate,
    allowComment,
    setAllowComment,
    allowDuet,
    setAllowDuet,
    allowStitch,
    setAllowStitch,
    commercial,
    setCommercial,
    yourBrand,
    setYourBrand,
    brandedContent,
    setBrandedContent,
    isAigc,
    setIsAigc,
    musicConfirmed,
    setMusicConfirmed,
    isComplete,
    variables,
    reset,
  };
}

export type TikTokPostInfoState = ReturnType<typeof useTikTokPostInfo>;
